import Component from '@averyano/core/src/classes/Component';
import GSAP from 'gsap';
import Hamburger from './Hamburger';
import each from 'lodash/each';
import NodeEmitter from '../classes/NodeEmitter';

const SCROLL_DIR_THRESHOLD = 5;
const SCROLL_TOP_BUFFER = 50;
const NAV_DUR = 0.6;
const NAV_EASE = 'power4.out';

export default class Navigation extends Component {
	constructor() {
		super({
			element: 'nav',
			elements: {
				content: '.nav__content',
				contentLinks: '.nav__content-link--inner',
				desktopLinks: '.nav__desktop-link',
				emailItem: '.nav__email-item',
				emailLink: '.nav__email-link',
				menuLogoPath: '.nav__logo-svg--main path',
				logoItem: '.nav__logo',
				logoOverlay: '.nav__logo--overlay path',
			},
		});
		this.isOpen = false;
		this.template = null;

		// Initial yPercent is -100 (paused fromTo); see createTimeline().
		this.navHidden = true;
		this.lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
		this.onScrollDirBound = this.onScrollDir.bind(this);

		this.createTimeline();
		this.createHamburgerIcon();

		this.addEventListeners();
	}

	/**
	 * HAMBURGER RELATED
	 */
	createHamburgerIcon() {
		this.hamburger = new Hamburger();
		this.hamburger.create();
	}

	openMenu() {
		this.isOpen = true;

		this.elements.emailLink.classList.add('dark');
		GSAP.to(this.elements.emailItem, {
			color: '#ffffff',
		});

		GSAP.to(this.elements.menuLogoPath, {
			fill: '#ffffff',
		});

		GSAP.to(this.elements.content, {
			autoAlpha: 1,
			onComplete: () => (this.hamburger.isAnimating = false),
		});

		GSAP.from(this.elements.contentLinks, {
			x: -1000,
			stagger: 0.1,
			duration: 1,
			ease: 'power4.out',
		});
	}

	closeMenu() {
		this.isOpen = false;

		this.elements.emailLink.classList.remove('dark');
		GSAP.to(this.elements.emailItem, {
			color: '#010101',
		});

		GSAP.to(this.elements.menuLogoPath, {
			fill: 'rgb(17,17,17)',
			onComplete: () => (this.hamburger.isAnimating = false),
		});

		GSAP.to(this.elements.content, {
			autoAlpha: 0,
		});
	}

	closeAll() {
		this.closeMenu();

		if (this.hamburger.isOpen) this.hamburger.toggleState();
	}
	/**
	 * MENU ELEMENTS RELATED
	 */
	// @TODO

	/**
	 * NAV RELATED
	 */
	createTimeline() {
		this.enterTl = GSAP.timeline();

		this.enterTl.fromTo(
			this.elements.logoOverlay,
			{
				opacity: 0,
			},
			{
				opacity: 1,
				duration: 0.2,
				stagger: 0.1,
				ease: 'out.expo',
			},
			0
		);

		this.leaveTl = GSAP.timeline();

		this.leaveTl.fromTo(
			this.elements.logoOverlay,
			{
				opacity: 1,
			},
			{
				opacity: 0,
				duration: 0.2,
				stagger: 0.1,
				ease: 'out.expo',
			},
			0
		);

		this.enterTl.pause();
		this.leaveTl.restart();

		// Start hidden above the viewport; show()/hide() tween yPercent directly
		// so the timing matches the .about coupling in ServicesSection.
		GSAP.set(this.element, { yPercent: -100 });
	}

	updateNav(template) {
		this.template = template;

		// Clear Classes
		each(this.elements.contentLinks, (link) => {
			link.classList.remove('active');
		});
		each(this.elements.desktopLinks, (link) => {
			link.classList.remove('active');
		});

		// Set Active Class based on page
		// Index maps to link order in navigation.pug:
		// desktop/content: 0 Home, 1 Websites, 2 Designs, 3 Portfolio, 4 (Print Shop / Book Consultation)
		// NOTE: Case Studies (cases/case) is temporarily hidden from nav — no active index.
		const activeIndex = {
			home: 0,
			websites: 1,
			designs: 2,
			portfolio: 3,
			store: 4,
			productview: 4,
		}[this.template];

		if (activeIndex !== undefined) {
			this.elements.desktopLinks[activeIndex]?.classList.add('active');
			this.elements.contentLinks[activeIndex]?.classList.add('active');
		}
	}

	show() {
		this.navHidden = false;
		GSAP.to(this.element, {
			yPercent: 0,
			duration: NAV_DUR,
			ease: NAV_EASE,
			overwrite: true,
		});
		this.$emit('nav:show');
	}

	hide() {
		this.navHidden = true;
		GSAP.to(this.element, {
			yPercent: -100,
			duration: NAV_DUR,
			ease: NAV_EASE,
			overwrite: true,
		});
		this.$emit('nav:hide');
	}

	onScrollDir() {
		if (this.isOpen) return;

		const y = window.scrollY;
		const dy = y - this.lastScrollY;
		if (Math.abs(dy) < SCROLL_DIR_THRESHOLD) return;
		this.lastScrollY = y;

		if (y < SCROLL_TOP_BUFFER) {
			if (this.navHidden) this.show();
			return;
		}

		if (dy > 0 && !this.navHidden) {
			this.hide();
		} else if (dy < 0 && this.navHidden) {
			this.show();
		}
	}

	addEventListeners() {
		window.addEventListener('scroll', this.onScrollDirBound, { passive: true });

		this.elements.logoItem.addEventListener('mouseenter', () => {
			this.leaveTl.pause();
			this.enterTl.restart();

			// to avoid the logo being white when the menu is open
			if (this.isOpen) {
				GSAP.to(this.elements.menuLogoPath, {
					fill: 'rgb(17,17,17)',
				});
			}
		});
		this.elements.logoItem.addEventListener('mouseleave', () => {
			this.enterTl.pause();
			this.leaveTl.restart();

			// to avoid the logo being white when the menu is open
			if (this.isOpen) {
				GSAP.to(this.elements.menuLogoPath, {
					fill: '#ffffff',
				});
			}
		});
	}
}
